import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/ui/use-toast';
import * as Api from '../api';
import type * as Types from '../types';

interface CreateTestData {
  title: string;
  description?: string;
  category?: string;
  organizationId: string;
  visibility?: 'PUBLIC' | 'ORG_ONLY';
  language?: string;
  settings?: {
    timeLimit?: number;
    passingScore?: number;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    showResultsImmediately?: boolean;
  };
  /** IELTS-style / Digital SAT practice format; omitted for a standard test. */
  examConfig?: Types.IApi.Create.Request['examConfig'];
}

const useCreate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateTestData) => {
      const response = await Api.Create({
        title: data.title,
        description: data.description,
        category: data.category,
        organizationId: data.organizationId,
        visibility: data.visibility,
        language: data.language,
        settings: data.settings,
        ...(data.examConfig ? { examConfig: data.examConfig } : {}),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test', 'list'] });
      toast.success('Success', { description: 'Test created successfully' });
    },
    onError: (error: any) => {
      toast.error('Error', {
        description: error?.response?.data?.message || 'Failed to create test',
      });
    },
  });
};

export default useCreate;
